import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/shared/components/ui/input";
import { Check, Copy, Play, Square, Eraser } from "lucide-react";
import { toast } from "sonner";
import {
  parseRawLogText,
  ServiceLogsClient,
  type ServiceLogLine,
} from "../services/serviceLogsService";
import type { SSESubscription } from "@/shared/utils/fetchSSE";
import { LogTerminal, LogTerminalRef } from "@/shared/components/ui/log-terminal";
import { LogLine } from "@/shared/components/ui/log-line";

// Generic Serilog terminal for the Agentic/Gateway tabs (PBI 81485). Behavioral
// clone of AssistantLogs parameterized by data client — the Assistant tab keeps its
// own component untouched (no-regression requirement).

const HISTORICAL_TAIL = 2000;
const MAX_LIVE_LINES = 2000;
const LIVE_OPTION_VALUE = "__live__";

const formatDateLabel = (fileName: string): string => {
  const match = fileName.match(/app-(\d{4})(\d{2})(\d{2})\.log/);
  if (!match) return fileName;
  return `${match[1]}-${match[2]}-${match[3]}`;
};

const todayLogFileName = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `app-${yyyy}${mm}${dd}.log`;
};

const renderLogLine = (log: ServiceLogLine) => (
  <LogLine timestamp={log.timestamp} level={log.level} message={log.message} />
);

interface ServiceLogTerminalProps {
  client: ServiceLogsClient;
  terminalTitle: string;
}

export const ServiceLogTerminal = ({ client, terminalTitle }: ServiceLogTerminalProps) => {
  const { t } = useTranslation();
  const [isPaused, setIsPaused] = useState(false);
  const [logLevel, setLogLevel] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [logs, setLogs] = useState<ServiceLogLine[]>([]);
  const [copied, setCopied] = useState(false);
  const [availableFiles, setAvailableFiles] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null); // null = live
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);
  const logTerminalRef = useRef<LogTerminalRef>(null);
  const eventSourceRef = useRef<SSESubscription | null>(null);
  const isPausedRef = useRef(isPaused);
  // Timer do feedback "copiado": guardado para ser cancelado no unmount (evita
  // setState em componente desmontado).
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    },
    []
  );

  const isLive = selectedFile === null;

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // Load list of available log files on mount
  useEffect(() => {
    client
      .getLogs()
      .then((response) => {
        setAvailableFiles(response.logFiles ?? []);
      })
      .catch(() => {
        toast.error(t("logs.failedToListFiles", "Could not load the list of available dates."));
      });
  }, [client, t]);

  const closeStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const openLiveStream = useCallback(() => {
    closeStream();
    // Initial tail = MAX_LIVE_LINES so the live view opens with the full day so far
    // (capped to the in-memory limit); subsequent lines arrive via SSE.
    eventSourceRef.current = client.createLogStream(
      (log) => {
        if (isPausedRef.current) return;
        setLogs((prev) =>
          prev.length >= MAX_LIVE_LINES ? [...prev.slice(-(MAX_LIVE_LINES - 1)), log] : [...prev, log]
        );
      },
      undefined,
      () => {
        toast.error(t("logs.connectionError", "Connection error"));
      },
      undefined,
      MAX_LIVE_LINES
    );
  }, [client, closeStream, t]);

  // Switch between live stream and historical snapshot when selection changes
  useEffect(() => {
    if (selectedFile === null) {
      setLogs([]);
      openLiveStream();
      return () => closeStream();
    }

    let cancelled = false;
    closeStream();
    setIsLoadingHistorical(true);
    setLogs([]);

    client
      .getLogs(selectedFile, HISTORICAL_TAIL)
      .then((response) => {
        if (cancelled) return;
        let lines: ServiceLogLine[] = (response.lines ?? []).map((line) => ({
          id: crypto.randomUUID(),
          timestamp: line.timestamp,
          level: line.level,
          message: line.message,
        }));
        // Fallback: upstream devolveu só o texto cru (sem `lines`) — parsear aqui
        // em vez de mostrar um terminal vazio com conteúdo disponível.
        if (lines.length === 0 && response.raw && response.raw.trim().length > 0) {
          lines = parseRawLogText(response.raw);
        }
        setLogs(lines);
        if (lines.length === 0 && (!response.raw || response.raw.trim().length === 0)) {
          toast.warning(
            t("logs.historicalUnavailable", "Historical file unavailable — it may have been rotated out.")
          );
        }
      })
      .catch(() => {
        if (cancelled) return;
        toast.error(
          t("logs.historicalUnavailable", "Historical file unavailable — it may have been rotated out.")
        );
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingHistorical(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedFile, client, openLiveStream, closeStream, t]);

  // Auto-scroll to the bottom when new lines arrive
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      logTerminalRef.current?.scrollToBottom();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [logs]);

  const togglePause = () => setIsPaused((prev) => !prev);

  const clearLogs = () => setLogs([]);

  // Today's file is covered by the "Live" option — drop it from the historical list.
  const historicalOptions = useMemo(() => {
    const today = todayLogFileName();
    return availableFiles
      .filter((f) => f !== today)
      .map((f) => ({ value: f, label: formatDateLabel(f) }));
  }, [availableFiles]);

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (logLevel !== "all") {
        const levelUpper = log.level.toUpperCase();
        const filterUpper = logLevel.toUpperCase();

        const levelMatch =
          levelUpper === filterUpper ||
          (filterUpper === "INF" && levelUpper === "INFO") ||
          (filterUpper === "WRN" && levelUpper === "WARN") ||
          (filterUpper === "ERR" && levelUpper === "ERROR") ||
          (filterUpper === "DBG" && levelUpper === "DEBUG");

        if (!levelMatch) return false;
      }

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const messageMatch = log.message.toLowerCase().includes(query);
        const timestampMatch = log.timestamp.toLowerCase().includes(query);
        const levelMatch = log.level.toLowerCase().includes(query);

        if (!messageMatch && !timestampMatch && !levelMatch) return false;
      }

      return true;
    });
  }, [logs, logLevel, searchQuery]);

  const copyLogs = () => {
    const logText = filteredLogs
      .map((log) => `[${log.timestamp}] [${log.level}] ${log.message}`)
      .join("\n");

    // Clipboard API is unavailable in insecure contexts and older browsers.
    if (!navigator.clipboard?.writeText) {
      toast.error(t("logs.copyError", "Erro ao copiar"));
      return;
    }

    navigator.clipboard
      .writeText(logText)
      .then(() => {
        setCopied(true);
        if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
        toast.success(t("logs.copied", "Logs copiados"));
        copiedTimerRef.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        toast.error(t("logs.copyError", "Erro ao copiar"));
      });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Input
            placeholder={t("logs.searchPlaceholder", "Buscar nos logs...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 max-w-md min-w-[180px]"
          />
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {t("logs.dateSelector", "Data")}:
            </span>
            <select
              value={selectedFile ?? LIVE_OPTION_VALUE}
              onChange={(e) =>
                setSelectedFile(e.target.value === LIVE_OPTION_VALUE ? null : e.target.value)
              }
              className="h-9 text-sm px-3 rounded-md border border-input bg-background"
            >
              <option value={LIVE_OPTION_VALUE}>{t("logs.liveOption", "Tempo real (hoje)")}</option>
              {historicalOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {t("logs.level", "Nível")}:
            </span>
            <select
              value={logLevel}
              onChange={(e) => setLogLevel(e.target.value)}
              className="h-9 text-sm px-3 rounded-md border border-input bg-background"
            >
              <option value="all">{t("logs.allLevels", "Todos os níveis")}</option>
              <option value="INF">INF</option>
              <option value="WRN">WRN</option>
              <option value="ERR">ERR</option>
              <option value="DBG">DBG</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm">
              {isLive ? (
                isPaused ? (
                  <span className="flex items-center text-yellow-500">
                    <span className="h-2 w-2 rounded-full bg-yellow-500 mr-2"></span>
                    {t("logs.paused", "Pausado")}
                  </span>
                ) : (
                  <span className="flex items-center text-green-500">
                    <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse mr-2"></span>
                    {t("logs.streamingActive", "Monitoramento em tempo real ativo")}
                  </span>
                )
              ) : (
                <span className="flex items-center text-blue-400">
                  <span className="h-2 w-2 rounded-full bg-blue-400 mr-2"></span>
                  {isLoadingHistorical
                    ? t("logs.loadingHistorical", "Carregando logs históricos...")
                    : t("logs.historicalSnapshot", "Snapshot histórico — sem atualização em tempo real")}
                </span>
              )}
            </span>
            {filteredLogs.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {t("logs.lineCount", { count: filteredLogs.length })}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="h-[500px] overflow-hidden">
        {logs.length === 0 && !isLoadingHistorical ? (
          <div className="flex flex-col items-center justify-center h-full border rounded-md bg-card text-muted-foreground">
            <p>{t("logs.waitingForLogs", "Aguardando logs...")}</p>
          </div>
        ) : (
          <LogTerminal
            className="h-full"
            ref={logTerminalRef}
            title={terminalTitle}
            actionButtons={
              <div className="flex space-x-3">
                {isLive &&
                  (isPaused ? (
                    <button
                      onClick={togglePause}
                      className="text-green-500 hover:text-green-700 focus:outline-none"
                      title={t("logs.resume", "Retomar")}
                    >
                      <Play className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={togglePause}
                      className="text-yellow-500 hover:text-yellow-700 focus:outline-none"
                      title={t("logs.pause", "Pausar")}
                    >
                      <Square className="h-4 w-4" />
                    </button>
                  ))}
                {isLive && (
                  <button
                    onClick={clearLogs}
                    className={`text-gray-500 hover:text-gray-700 focus:outline-none ${
                      logs.length === 0 ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    title={t("logs.clearLogs", "Limpar")}
                    disabled={logs.length === 0}
                  >
                    <Eraser className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={copyLogs}
                  className={`text-gray-500 hover:text-gray-700 focus:outline-none ${
                    filteredLogs.length === 0 ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  title={t("logs.copyLogsTitle", "Copiar Logs")}
                  disabled={filteredLogs.length === 0}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            }
          >
            {filteredLogs.map((log) => (
              <div key={log.id} className="py-0.5 whitespace-pre-wrap break-all">
                {renderLogLine(log)}
              </div>
            ))}
            {isLive && !isPaused && (
              <div className="h-4 flex items-center mt-1">
                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse mr-2"></div>
                <span className="text-xs text-green-600">
                  {t("logs.liveStreaming", "Streaming ao vivo")}
                </span>
              </div>
            )}
          </LogTerminal>
        )}
      </div>
    </div>
  );
};
